import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { RCCGrid, RCCHookToast, RCCInput, RCCRefModalProps, RCCServicesApi, RCCSwitch, RCCTextArea} from "react-common-components";
import { useForm } from "react-hook-form";
import Modal from "src/components/Modal";
import { useTranslation } from "src/context/Translations/context";
import { getValidationErrors, standardErrorMessages } from "src/utils/errors";
import Permissions from "src/components/Permissions";
import { ISidebarCardItem } from "src/components/SidebarCard/interface";
import Audit from "src/components/Audit";
import { useAuth } from "src/context/Auth/context";
import Tabs from "src/components/Tabs";
import { simplePermissionType } from "src/components/Permissions/interface";
import { v4 } from "uuid";
import DeleteModal from "src/components/DeleteModal";
import DateTimePicker from "src/components/DateTimePickerRange";
import ModalSide from "src/components/Modal/styles";
import LovTags from "src/components/Lov/Tags";
import extractSingleValueFromArrayOfObject from "src/utils/extractSingleValueFromArrayOfObject";
import { IMapsModal, IRefMapsModals, IMetba008, IMetba001Form, ISidebarCardItemMaps } from "./interface";
import {deleteSchemaMap, addAndChangeSchemaMap} from "./schemas";
import { IMetba002 } from "./Components/Perspective/interface";

const Modals: React.ForwardRefRenderFunction<IRefMapsModals, IMapsModal> = ({
	cards,
	setCards,
	mapsSubtitle,
	onCallbackDeleteMap,
	onCallbackMaps
},ref) => {
	const { translate } = useTranslation();
	const { user } = useAuth();
	const {addToast} = RCCHookToast();
	const [isCopy, setIsCopy] = useState(false);

	const [mapSelected, setMapSelected] = useState<IMetba001Form>();
	const [mapDelete, setMapDelete] = useState<ISidebarCardItem>();

	const standardPerspectives = () => {
		const perspectives: IMetba002[] =[{
			id: v4(),
			mea002Id: 0,
			mea002Mea001Id: 0,
			mea002Nome: translate("Financeira"),
			mea002Descricao: translate("Esta é uma consequência das demais perspectivas. Atendendo bem os objetivos estratégicos das demais perspectivas, os objetivos financeiros devem ser alcançados."),
			mea002Cor: "#D64541",
			mea002Ordem: 0,
			mea002Peso: 1
		},{
			id: v4(),
			mea002Id: 0,
			mea002Mea001Id: 0,
			mea002Nome: translate("Clientes"),
			mea002Descricao: translate("Os clientes estando bem atendidos e satisfeitos teremos como consequência um maior retorno financeiro pois clientes satisfeitos compram mais."),
			mea002Cor: "#2ABB9B",
			mea002Ordem: 0,
			mea002Peso: 1
		},{
			id: v4(),
			mea002Id: 0,
			mea002Mea001Id: 0,
			mea002Nome: translate("Processos Internos"),
			mea002Descricao: translate("Melhorando os processos internos iremos atender melhor os clientes."),
			mea002Cor: "#BE90D4",
			mea002Ordem: 0,
			mea002Peso: 1
		},{
			id: v4(),
			mea002Id: 0,
			mea002Mea001Id: 0,
			mea002Nome: translate("Aprendizado e Crescimento"),
			mea002Descricao: translate("Pessoas bem treinadas e capacitadas conseguem atender e realizar melhor os processos internos."),
			mea002Cor: "#4B77BE",
			mea002Ordem: 0,
			mea002Peso: 1
		}];

		return perspectives;
	};

	// Modal Add/Change
	const [isAdd, setIsAdd] = useState(false);
	const modalAddOrChange = useRef<RCCRefModalProps>(null);
	const { 
		handleSubmit : handleSubmitAddOrChange, 
		register : registerAddOrChange, 
		control: controlAddOrChange, 
		reset : resetAddOrChange, 
		setError : setErrorAddOrChange, 
		formState: { errors : errorsAddOrChange }, 
		clearErrors: clearErrorsAddOrChange, 
		unregister: unRegisterForm 
	} = useForm();

	const actionButtonAdd = () => {
		resetAddOrChange(); 
		setIsAdd(true);
		setIsCopy(false);

		const permission: IMetba008[] = [{mea008Id: 0, mea008Permissao: Number(simplePermissionType.VIEW_AND_EDIT), mea008Efa005Id: user.efa005Id, mea008Efa005Nome: user.efa005Nome}];
		const map = {} as IMetba001Form;

		setMapSelected({
			...map,
			metba008: permission
		});
		
		modalAddOrChange.current?.openModal();
	};

	const actionButtonChange = (card: ISidebarCardItem, copy: boolean) => {
		resetAddOrChange(); 
		setIsCopy(copy);
		setIsAdd(false);
		getMap(card.id);
	};

	// Modal Delete
	const modalDelete = useRef<RCCRefModalProps>(null);
	const { 
		handleSubmit: handleSubmitDelete, 
		register: registerDelete, 
		reset : resetDelete, 
		setError : setErrorDelete, 
		formState: { errors : errorsDelete }, 
		clearErrors: clearErrorsDelete 
	} = useForm();
	
	const actionButtonDelete = (card: ISidebarCardItem) => {
		resetDelete();
		setMapDelete(card);
		modalDelete.current?.openModal();
	};

	const submitAddOrChange = async (data: any) => {
		let msgToastSuccess = "";
		let msgToastError = "";

		try{
			clearErrorsAddOrChange();
			modalAddOrChange.current?.setLoadingButton();
			
			const dataForm = data;
			if(data.mea001Tags)
				dataForm.mea001Tags = extractSingleValueFromArrayOfObject("value", data.mea001Tags);
			else
				dataForm.mea001Tags = null;
				
			dataForm.mea001Ativo = data.mea001Ativo ? "S" : "N";

			if(data.metba008 && data.metba008.length > 0){
				dataForm.metba008 = data.metba008.map((mea008: any) => {
					if(typeof mea008.mea008Efa005Id === "object")
						return {
							...mea008,
							mea008Permissao: Number(mea008.mea008Permissao),
							mea008Efa005Id: mea008.mea008Efa005Id.value
						};
					if(typeof mea008.mea008Efa004Id === "object")
						return {
							...mea008,
							mea008Permissao: Number(mea008.mea008Permissao),
							mea008Efa004Id: mea008.mea008Efa004Id.value
						};
					return mea008;
				});

				// verifica se tem permissão de Visializar e Editar
				if(!dataForm.metba008.filter((x:any) => Number(x.mea008Permissao) === Number(simplePermissionType.VIEW_AND_EDIT)).length){
					modalAddOrChange.current?.resetLoadingButton();
					addToast({
						color: "yellow",
						description: translate("Ao menos uma permissão deve ser (Visualizar e Editar)"),
						title: translate("Atenção")
					});
					return;
				}
				
				if(data.metba008 && data.metba008.length > 0){
					dataForm.metba008 = data.metba008.filter((dt: any) => dt.mea008Efa005Id !== 0);
					dataForm.metba008 = data.metba008.filter((dt: any) => dt.mea008Efa004Id !== 0);
				}
			}
			
			await addAndChangeSchemaMap(translate).validate({...dataForm}, {
				abortEarly: false
			});

			if(isAdd){
				const result = await RCCServicesApi.postRequest({
					method: "Maps/Insert",
					body: {
						...dataForm,
						mea001Id: 0,
						metba002: standardPerspectives()			
					}
				});

				onCallbackMaps(result.data);

				msgToastSuccess=translate("Mapa adicionado com sucesso");
				msgToastError=translate("Erro ao adicionar mapa");

			}else if(isCopy){
				const result = await RCCServicesApi.putRequest({
					method: "Maps/Copy",
					body: {
						...dataForm
					}
				});

				msgToastSuccess=translate("Mapa copiado com sucesso");
				msgToastError=translate("Erro ao copiar mapa");

				onCallbackMaps(result.data);
			
			}else{
				await RCCServicesApi.putRequest({
					method: "Maps/Edit",
					body: {
						...dataForm
					}
				});

				setCards(cards.map((x: ISidebarCardItemMaps) => {
					const newMap = x;
					if(newMap.id === Number(dataForm.mea001Id)){
						newMap.title = dataForm.mea001Nome;
						newMap.subtitle = mapsSubtitle(dataForm.mea001Dtvalini, dataForm.mea001Dtvalfim);
					}
					return newMap;
				}));

				onCallbackMaps(dataForm.mea001Id);

				msgToastSuccess=translate("Mapa alterado com sucesso");
				msgToastError=translate("Erro ao alterar mapa");
			}

			addToast({
				color: "green",
				description: msgToastSuccess,
				title: translate("Sucesso")
			});

			modalAddOrChange.current?.resetLoadingButton();
			modalAddOrChange.current?.closeModal();

		}catch(err){

			modalAddOrChange.current?.resetLoadingButton();
			if(getValidationErrors(err, setErrorAddOrChange)){
				addToast({
					color: "yellow",
					description: translate("Verifique os campos e tente novamente"),
					title: translate("Atenção")
				});
				return;
			}
				
			standardErrorMessages(err, msgToastError, addToast);
		}
	};

	const submitDelete = async (data: any) => {
		try{
			clearErrorsDelete();
			modalDelete.current?.setLoadingButton();
			
			await deleteSchemaMap(translate).validate({...data, "mea001NomeConfirmation": mapDelete?.title},{
				abortEarly: false
			});
			
			await RCCServicesApi.deleteRequest({
				method: "Maps/Delete",
				urlParameters: {
					id: mapDelete?.id,
					title: mapDelete?.title
				}
			});

			addToast({
				color: "green",
				description: translate("Mapa excluído com sucesso" ),
				title: translate("Sucesso")
			});

			onCallbackDeleteMap(mapDelete?.id || 0);

			modalDelete.current?.resetLoadingButton();
			modalDelete.current?.closeModal();

		}catch(err){

			modalDelete.current?.resetLoadingButton();
			if(getValidationErrors(err, setErrorDelete))
				return;

			standardErrorMessages(err, translate("Erro ao excluir mapa"), addToast);
		}
	};

	useImperativeHandle(ref, () => ({
		actionButtonAdd,
		actionButtonChange,
		actionButtonDelete
	}));

	async function getMap(idMap: number){
		try{
			const result = await RCCServicesApi.getRequest({
				method: "Maps/Select",
				urlParameters: {id: idMap}
			});
			setMapSelected(result.data);
			modalAddOrChange.current?.openModal();
		}catch(err){
			standardErrorMessages(err, translate("Erro ao buscar detalhes do mapa"), addToast);
		}
	}

	const titleModal = () => {
		if(isAdd) return translate("Adicionar Mapa");
		if(isCopy) return translate("Copiar Mapa");
		return translate("Alterar Mapa");
	};

	const iconModal = () => {
		if(isAdd) return "plus";
		if(isCopy) return "copy";
		return "pencil";
	};

	return (
		<>
			{/* Modal Delete */}
			<DeleteModal
				ref={modalDelete}
				title= {translate("Exclusão do Mapa")}
				description= {`${translate("Deseja realmente excluir o mapa")}: ${mapDelete?.title}?`}
				onSubmit = {handleSubmitDelete(submitDelete)}
				height="250px"
			>
				<RCCGrid
					container
					item
					xs={10}
				>
					<RCCInput 
						register={registerDelete}
						name="mea001Nome"
						label={translate("Digite o nome do Mapa para excluí-lo")}
						maxLength={200}
						error={errorsDelete.mea001Nome}
						required
					/>
				</RCCGrid>
			</DeleteModal>

			{/* Modal Add an Change */}
			<Modal
				ref={modalAddOrChange}
				header={{
					title: titleModal(),
					subtitle: !isAdd ? `${translate("Mapa")}: ${mapSelected?.mea001Nome}` : "",
					icon: {
						name: iconModal()
					}
				}}
				onSubmit={handleSubmitAddOrChange(submitAddOrChange)}
				footer={{
					showSaveButton: true,
					buttonLoadingText: translate("Carregando"),
					saveButtonText: isCopy ? translate("Copiar") : translate("Salvar"),
					cancelButtonText: translate("Cancelar")
				}}
				styleInline={ModalSide}
			>
				<Tabs
					name="tabMapa"
					tabInit={0}
					options={[
						{
							label: translate("Geral"),
							content: <RCCGrid
								container
								item
								direction="column"
								alignItems="center"
							>
								<RCCGrid
									container
									item
									alignItems="center"
									justifyContent="flex-end"
								>
									{
										mapSelected && <Audit data={mapSelected} type="pop"/>
									}
								</RCCGrid>
								<RCCGrid styleInline="display: none">
									<RCCInput 
										register={registerAddOrChange}
										name="mea001Id"
										label=""
										defaultValue={mapSelected?.mea001Id}
										readOnly
									/>
								</RCCGrid>
								<RCCGrid
									container
									item
								>
									<RCCInput 
										register={registerAddOrChange}
										name="mea001Nome"
										label={translate("Nome")}
										maxLength={100}
										defaultValue={isCopy ? `(${translate("CÓPIA")}) ${mapSelected?.mea001Nome}` : mapSelected?.mea001Nome}
										error={errorsAddOrChange.mea001Nome}
										required
									/>
								</RCCGrid>
								<RCCGrid
									container
									item
								>
									<RCCGrid 
										container
										item
									>
										<DateTimePicker
											control={controlAddOrChange}
											type="datePicker"
											label={translate("Período")}
											labelSeparator={translate("Até")}
											nameInit="mea001Dtvalini"
											nameEnd="mea001Dtvalfim"
											defaultValueInit={mapSelected?.mea001Dtvalini ? mapSelected.mea001Dtvalini : new Date(new Date().getFullYear(), 0, 1)}
											defaultValueEnd={mapSelected?.mea001Dtvalfim ? mapSelected.mea001Dtvalfim : new Date(new Date().getFullYear(), 11, 31)}
											error={errorsAddOrChange.mea001Dtvalini || errorsAddOrChange.mea001Dtvalfim}
										/>
									</RCCGrid>								
								</RCCGrid>
								<RCCGrid
									container
									item
								>
									<RCCTextArea 
										register={registerAddOrChange}
										name="mea001Descricao"
										label={translate("Descrição")}
										maxLength={200}
										defaultValue={mapSelected?.mea001Descricao}
										error={errorsAddOrChange.mea001Descricao}
										required
									/>
								</RCCGrid>
								<RCCGrid
									container
									item
								>
									<RCCTextArea 
										register={registerAddOrChange}
										name="mea001Missao"
										label={translate("Missão")}
										maxLength={2000}
										defaultValue={mapSelected?.mea001Missao}
										error={errorsAddOrChange.mea001Missao}
										required
									/>
								</RCCGrid>
								<RCCGrid 
									container 
									item 
								>
									<RCCTextArea
										register={registerAddOrChange}
										name="mea001Visao" 
										label={translate("Visão")} 
										maxLength={2000}
										defaultValue={mapSelected?.mea001Visao}
										error={errorsAddOrChange.mea001Visao}
										required
									/>
								</RCCGrid>
								<RCCGrid
									container
									item
								>
									<RCCTextArea 
										register={registerAddOrChange}
										name="mea001Notas"
										label={translate("Notas")}
										maxLength={2000}
										defaultValue={mapSelected?.mea001Notas}
									/>
								</RCCGrid>
								<RCCGrid
									container
									item
								>
									<RCCTextArea 
										register={registerAddOrChange}
										name="mea001Politicaqualidade"
										label={translate("Política de Qualidade")}
										maxLength={2000}
										defaultValue={mapSelected?.mea001Politicaqualidade}
									/>
								</RCCGrid>
								<RCCGrid
									container
									item
								>
									<RCCTextArea 
										register={registerAddOrChange}
										name="mea001Valores"
										label={translate("Valores")}
										maxLength={2000}
										defaultValue={mapSelected?.mea001Valores}
									/>
								</RCCGrid>
								<RCCGrid
									container
									item
								>
									<LovTags 
										control={controlAddOrChange}
										name="mea001Tags"
										label={translate("Tags")}
										defaultValue={Array.isArray(mapSelected?.mea001Tags) ? mapSelected?.mea001Tags.join(",") : ""}
										extraParametersOnLoad={{
											use: 9
										}}
										extraParametersOnCreate={{
											use: 9
										}}
									/>
								</RCCGrid>
								<RCCGrid 
									container
									item
								>
									<RCCSwitch 
										name="mea001Ativo" 
										label={translate("Ativo")} 
										register={registerAddOrChange} 
										defaultChecked={mapSelected?.mea001Ativo !== "N"}
									/>
								</RCCGrid>
							</RCCGrid>
						},
						{
							label: translate("Permissões"),
							content: 
							<Permissions
								register={registerAddOrChange}
								unregister={unRegisterForm}
								control={controlAddOrChange}
								error={errorsAddOrChange}
								clearErrors={clearErrorsAddOrChange}
								permissionsNames= {{
									nameObject: "metba008",
									nameOfID: "mea008Id",
									nameOfSelectUserDescription: "mea008Efa005Nome",
									nameOfSelectGroupDescription: "mea008Efa004Nome",
									nameOfSelectUser: "mea008Efa005Id",
									nameOfSelectGroup: "mea008Efa004Id",
									nameOfSelectPermission: "mea008Permissao",
									defaultValue: mapSelected?.metba008
								}}
							/>
						}
					]}
				/>
			</Modal>
		</>
	);
};

export default forwardRef(Modals);
